import React from "react";
import {
  Card,
  Elevation,
  Tag,
  Button,
  H1,
  H3,
  Text,
  InputGroup,
  Intent,
  Icon,
  Tooltip,
  Position,
  NonIdealState,
  Spinner,
  FormGroup,
  ControlGroup,
  Switch,
  TextArea,
} from "@blueprintjs/core";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { LicenseManager } from "ag-grid-enterprise";
import "ag-grid-enterprise";
import { inject, observer } from "mobx-react";
import { apiCallPost } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import _ from "lodash";
import BranchService from "../../../micro/services/BranchService";
import AuditService from "../../../micro/services/AuditService";
import NewMultiSelector from "../../../micro/components/NewMultiSelector";
import Modal from "../../../micro/components/Modal";
import AuditLogViewer from "../../../micro/components/AuditLogViewer";

// Set AG Grid license
LicenseManager.setLicenseKey("");

class TPAMaster extends React.Component {
  state = {
    // Data
    tpas: [],
    branches: [],

    // Loading states
    isLoading: true,
    isSaving: false,

    // Grid state
    gridApi: null,
    columnApi: null,
    quickFilterText: "",

    // Form state for adding new TPA
    newTPA: {
      tpaName: "",
      tpaCode: "",
      contactPerson: "",
      contactPhone: "",
      contactEmail: "",
      address: "",
      isActive: true,
      branches: [],
      remarks: "",
    },

    // UI state for new TPA modal
    showNewTPAModal: false,

    // Edit modal state
    isEditModalOpen: false,
    editingTPA: null,

    // Audit log state
    showAuditLogs: false,
  };

  componentDidMount() {
    this.loadData();
  }

  onGridReady = (params) => {
    this.setState({ gridApi: params.api, columnApi: params.columnApi });
  };

  columnDefs = [
    {
      headerName: "TPA Name",
      field: "tpaName",
      width: 200,
      filter: "agTextColumnFilter",
      pinned: "left",
      editable: false,
    },
    {
      headerName: "TPA Code",
      field: "tpaCode",
      width: 120,
      filter: "agTextColumnFilter",
      editable: false,
    },
    {
      headerName: "Contact Person",
      field: "contactPerson",
      width: 150,
      filter: "agTextColumnFilter",
      editable: false,
    },
    {
      headerName: "Contact Phone",
      field: "contactPhone",
      width: 140,
      filter: "agTextColumnFilter",
      editable: false,
    },
    {
      headerName: "Status",
      field: "isActive",
      width: 120,
      filter: "agSetColumnFilter",
      cellRenderer: (params) => (params.value ? "Active" : "Inactive"),
      editable: false,
    },
    {
      headerName: "Branches",
      field: "branches",
      width: 200,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        if (!params.value || params.value.length === 0) {
          return "No branches";
        }

        if (params.value.length <= 2) {
          return params.value.join(", ");
        }

        return params.value.slice(0, 2).join(", ") + ` (+${params.value.length - 2} more)`;
      },
      editable: false,
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 120,
      cellRenderer: (params) => {
        const container = document.createElement("div");
        const editBtn = this.createActionButton("✏️", "#137cbd", () => this.editTPA(params.data));
        const deleteBtn = this.createActionButton("🗑️", "#db3737", () => this.deleteTPA(params.data._id));
        container.appendChild(editBtn);
        container.appendChild(deleteBtn);
        return container;
      },
      filter: false,
      sortable: false,
    },
  ];

  createActionButton = (text, backgroundColor, onClick) => {
    const button = document.createElement("button");
    button.innerHTML = text;
    button.style.marginRight = "5px";
    button.style.padding = "4px 8px";
    button.style.border = `1px solid ${backgroundColor}`;
    button.style.backgroundColor = backgroundColor;
    button.style.color = "white";
    button.style.borderRadius = "3px";
    button.style.cursor = "pointer";
    button.onclick = onClick;
    return button;
  };

  loadData = async () => {
    try {
      this.setState({ isLoading: true });
      cogoToast.loading("Loading TPA data...");

      // Load TPAs and branches in parallel
      await Promise.all([this.loadTPAs(), this.loadBranches()]);

      cogoToast.success("Data loaded successfully");
    } catch (err) {
      cogoToast.error("Failed to load data. Please try again.");
      console.error("Detailed error loading data:", err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  loadBranches = async () => {
    try {
      const branchNames = await BranchService.getBranchNames(this.props.authStore.prefix);
      this.setState({ branches: branchNames });
      console.log("✅ DEBUG: Branches loaded successfully:", branchNames.length);
    } catch (err) {
      console.error("Failed to load branches:", err);
      // Fallback to dummy branches
      this.setState({
        branches: ["Newtown Branch", "Park Street Branch", "Salt Lake Branch", "Howrah Branch"],
      });
      console.warn("⚠️ DEBUG: Using fallback branches due to API error");
    }
  };

  loadTPAs = async () => {
    try {
      console.log("🔍 DEBUG: Loading TPAs from API");
      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxTPAMaster/getAll`, {});
      this.setState({ tpas: response || [] });
      console.log("✅ DEBUG: TPAs loaded successfully:", (response || []).length);
    } catch (err) {
      console.error("Failed to load TPAs:", err);
      // Fallback to static data if API fails
      this.initializeStaticTPAs();
      console.warn("⚠️ DEBUG: Using static data due to API error");
    }
  };

  initializeStaticTPAs = () => {
    const allBranches =
      this.state.branches.length > 0
        ? this.state.branches
        : ["Newtown Branch", "Park Street Branch", "Salt Lake Branch", "Howrah Branch"];

    const staticTPAs = [
      {
        _id: "1",
        tpaName: "Vidal Health TPA",
        tpaCode: "VHT001",
        contactPerson: "Rajesh Kumar",
        contactPhone: "+91-9876543210",
        contactEmail: "rajesh@vidalhealth.com",
        address: "123 Business District, Kolkata - 700001",
        isActive: true,
        branches: [...allBranches],
        remarks: "Premium TPA partner with excellent claim processing",
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "2",
        tpaName: "Medi Assist India",
        tpaCode: "MAI002",
        contactPerson: "Priya Sharma",
        contactPhone: "+91-9876543211",
        contactEmail: "priya@mediassist.in",
        address: "456 Healthcare Avenue, Mumbai - 400001",
        isActive: true,
        branches: [...allBranches],
        remarks: "Large network TPA with quick approvals",
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "3",
        tpaName: "Paramount Health Services",
        tpaCode: "PHS003",
        contactPerson: "Amit Gupta",
        contactPhone: "+91-9876543212",
        contactEmail: "amit@paramounthealth.com",
        address: "789 Medical Complex, Delhi - 110001",
        isActive: true,
        branches: allBranches.slice(0, 2), // Only first 2 branches
        remarks: "Specialized in corporate health insurance",
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "4",
        tpaName: "Family Health Plan TPA",
        tpaCode: "FHP004",
        contactPerson: "Sunita Rao",
        contactPhone: "+91-9876543213",
        contactEmail: "sunita@familyhealth.co.in",
        address: "321 Insurance Hub, Bangalore - 560001",
        isActive: false,
        branches: allBranches.slice(1, 3), // Middle 2 branches
        remarks: "Currently under review for service quality",
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "5",
        tpaName: "United India Insurance TPA",
        tpaCode: "UII005",
        contactPerson: "Vikram Singh",
        contactPhone: "+91-9876543214",
        contactEmail: "vikram@unitedindia.com",
        address: "654 Government Sector, Chennai - 600001",
        isActive: true,
        branches: [...allBranches],
        remarks: "Government-backed TPA with high credit limits",
        createdAt: new Date(),
        updatedAt: new Date(),
      },
    ];

    this.setState({ tpas: staticTPAs });
  };

  editTPA = (tpa) => {
    // Open edit modal with prefilled values
    this.setState({
      isEditModalOpen: true,
      editingTPA: { ...tpa },
    });
  };

  updateTPAFromModal = async () => {
    const { editingTPA } = this.state;

    // Validation
    if (!editingTPA.tpaName.trim()) {
      cogoToast.error("TPA Name is required");
      return;
    }

    if (!editingTPA.tpaCode.trim()) {
      cogoToast.error("TPA Code is required");
      return;
    }

    if (!editingTPA.branches.length) {
      cogoToast.error("At least one branch must be selected");
      return;
    }

    // Check for duplicate TPA names and codes
    const isDuplicateName = this.state.tpas.some((existingTPA) => {
      if (existingTPA._id === editingTPA._id) return false;
      return existingTPA.tpaName === editingTPA.tpaName.trim();
    });

    const isDuplicateCode = this.state.tpas.some((existingTPA) => {
      if (existingTPA._id === editingTPA._id) return false;
      return existingTPA.tpaCode === editingTPA.tpaCode.trim();
    });

    if (isDuplicateName) {
      cogoToast.error(`TPA name "${editingTPA.tpaName.trim()}" already exists. Please choose a different name.`);
      return;
    }

    if (isDuplicateCode) {
      cogoToast.error(`TPA code "${editingTPA.tpaCode.trim()}" already exists. Please choose a different code.`);
      return;
    }

    try {
      cogoToast.loading("Updating TPA...");
      this.setState({ isSaving: true });

      const updatePayload = {
        _id: editingTPA._id,
        tpaName: editingTPA.tpaName.trim(),
        tpaCode: editingTPA.tpaCode.trim(),
        contactPerson: editingTPA.contactPerson.trim(),
        contactPhone: editingTPA.contactPhone.trim(),
        contactEmail: editingTPA.contactEmail.trim(),
        address: editingTPA.address.trim(),
        isActive: editingTPA.isActive,
        branches: editingTPA.branches,
        remarks: editingTPA.remarks.trim(),
      };

      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxTPAMaster/update`, updatePayload);

      if (response && response._id) {
        const updatedTPA = { ...response };

        this.setState((prevState) => ({
          tpas: (prevState.tpas || []).map((tpa) => (tpa._id === editingTPA._id ? updatedTPA : tpa)),
          isSaving: false,
          isEditModalOpen: false,
          editingTPA: null,
        }));

        // Log audit event for TPA update
        try {
          const auditResult = await AuditService.logEntityUpdate({
            entityType: "tpaMaster",
            entityId: editingTPA._id,
            oldData: this.state.tpas.find((t) => t._id === editingTPA._id),
            newData: updatedTPA,
            authStore: this.props.authStore,
            description: `Updated TPA "${updatedTPA.tpaName}"`,
          });
          console.log("✅ DEBUG: Audit log updated successfully", auditResult);
        } catch (auditError) {
          console.error("❌ DEBUG: Failed to create audit log for update", auditError);
        }

        cogoToast.success("TPA updated successfully!");
      } else {
        throw new Error("Invalid response from update API");
      }
    } catch (err) {
      console.error("❌ DEBUG: Error updating TPA:", err);
      cogoToast.error("Failed to update TPA. Please try again.");
      this.setState({ isSaving: false });
    }
  };

  closeEditModal = () => {
    this.setState({
      isEditModalOpen: false,
      editingTPA: null,
    });
  };

  handleNewTPASubmit = () => {
    const { newTPA } = this.state;

    if (!newTPA.tpaName.trim()) {
      cogoToast.error("Please enter a TPA name");
      return;
    }

    if (!newTPA.branches.length) {
      cogoToast.error("Please select at least one branch");
      return;
    }

    // Check for duplicates
    const isDuplicateName = this.state.tpas.some((existingTPA) => existingTPA.tpaName === newTPA.tpaName.trim());

    const trimmedNewCode = newTPA.tpaCode.trim();
    const isDuplicateCode = trimmedNewCode
      ? this.state.tpas.some((existingTPA) => (existingTPA.tpaCode || "").trim() === trimmedNewCode)
      : false;

    if (isDuplicateName) {
      cogoToast.error(`TPA name "${newTPA.tpaName.trim()}" already exists. Please choose a different name.`);
      return;
    }

    if (isDuplicateCode) {
      cogoToast.error(`TPA code "${trimmedNewCode}" already exists. Please choose a different code.`);
      return;
    }

    this.createNew();
  };

  createNew = async () => {
    this.setState({ isSaving: true });
    cogoToast.loading("Creating new TPA Master");

    try {
      console.log("🔍 DEBUG: Creating new TPA via API");

      // Create new TPA via API
      const newTPAData = {
        tpaName: this.state.newTPA.tpaName.trim(),
        tpaCode: (this.state.newTPA.tpaCode || "").trim(),
        contactPerson: this.state.newTPA.contactPerson.trim(),
        contactPhone: this.state.newTPA.contactPhone.trim(),
        contactEmail: this.state.newTPA.contactEmail.trim(),
        address: this.state.newTPA.address.trim(),
        isActive: this.state.newTPA.isActive,
        branches: this.state.newTPA.branches,
        remarks: this.state.newTPA.remarks.trim(),
      };

      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxTPAMaster/create`, newTPAData);

      if (response && response._id) {
        const newTPA = { ...response };

        // Log audit event for TPA creation
        try {
          const auditResult = await AuditService.logEntityCreate({
            entityType: "tpaMaster",
            entityId: newTPA._id,
            newData: newTPA,
            authStore: this.props.authStore,
            description: `Created new TPA "${newTPA.tpaName || "Unnamed TPA"}"`,
          });
          console.log("✅ DEBUG: Audit log created successfully", auditResult);
        } catch (auditError) {
          console.error("❌ DEBUG: Failed to create audit log", auditError);
        }

        this.setState({
          tpas: [newTPA, ...this.state.tpas],
          isSaving: false,
          showNewTPAModal: false,
          newTPA: {
            tpaName: "",
            tpaCode: "",
            contactPerson: "",
            contactPhone: "",
            contactEmail: "",
            address: "",
            isActive: true,
            branches: [],
            remarks: "",
          },
        });
        cogoToast.success("TPA created successfully!");
      } else {
        throw new Error("Invalid response from create API");
      }
    } catch (err) {
      console.error("❌ DEBUG: Error creating TPA:", err);
      cogoToast.error("Failed to create new TPA. Please try again.");
      this.setState({ isSaving: false });
    }
  };

  deleteTPA = async (_id) => {
    this.setState({ isSaving: true });
    cogoToast.loading("Deleting TPA...");

    // Find the TPA data for audit logging
    const tpaToDelete = this.state.tpas.find((tpa) => tpa._id === _id);

    try {
      console.log("🔍 DEBUG: Deleting TPA via API", { _id });

      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxTPAMaster/delete`, {
        _id,
      });

      if (response && response.success !== false) {
        this.setState((prevState) => ({
          tpas: (prevState.tpas || []).filter((tpa) => tpa._id !== _id),
          isSaving: false,
        }));

        // Log audit event for TPA deletion
        if (tpaToDelete) {
          try {
            const auditResult = await AuditService.logEntityDelete({
              entityType: "tpaMaster",
              entityId: _id,
              deletedData: tpaToDelete,
              authStore: this.props.authStore,
              description: `Deleted TPA "${tpaToDelete.tpaName}"`,
            });
            console.log("✅ DEBUG: Audit log deleted successfully", auditResult);
          } catch (auditError) {
            console.error("❌ DEBUG: Failed to create audit log for deletion", auditError);
          }
        }

        cogoToast.success("TPA deleted successfully!");
      } else {
        throw new Error("Delete operation failed or was rejected");
      }
    } catch (err) {
      console.error("❌ DEBUG: Error deleting TPA:", err);
      cogoToast.error("Failed to delete TPA. Please try again.");
      this.setState({ isSaving: false });
    }
  };

  handleShowAuditLogs = () => {
    this.setState({ showAuditLogs: true });
  };

  handleCloseAuditLogs = () => {
    this.setState({ showAuditLogs: false });
  };

  renderHeader = () => {
    const { quickFilterText, isSaving, tpas, branches } = this.state;

    return (
      <div style={{ marginBottom: "var(--v3-spacing-lg)" }}>
        <H1 style={{ color: "var(--text-primary)", margin: 0 }}>TPA Master</H1>
        <Text
          style={{
            color: "var(--text-secondary)",
            margin: "var(--v3-spacing-xs) 0",
            fontSize: "var(--v3-font-size-md)",
            fontFamily: "var(--v3-font-family-sans)",
          }}
        >
          Manage Third Party Administrators (TPAs) and their service configurations
        </Text>

        <Card elevation={Elevation.TWO} style={{ padding: "var(--v3-spacing-lg)", marginTop: "var(--v3-spacing-lg)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "var(--v3-spacing-md)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1 }}>
              <div style={{ minWidth: "300px" }}>
                <InputGroup
                  leftIcon="search"
                  placeholder="Search TPAs, codes, or contact details..."
                  value={quickFilterText}
                  onChange={(e) => this.setState({ quickFilterText: e.target.value })}
                  disabled={isSaving}
                />
              </div>

              <div style={{ display: "flex", gap: "8px" }}>
                <Tag large intent="primary" icon="numerical">
                  {tpas.length} TPAs
                </Tag>
                <Tag large intent="success" icon="map-marker">
                  {branches.length} Branches
                </Tag>
                <Tag large intent="warning" icon="tick-circle">
                  {tpas.filter((tpa) => tpa.isActive).length} Active
                </Tag>
              </div>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              <Tooltip content="Refresh data" position={Position.BOTTOM}>
                <Button
                  icon="refresh"
                  disabled={isSaving}
                  onClick={() => {
                    this.loadBranches();
                    this.loadTPAs();
                  }}
                />
              </Tooltip>

              <Tooltip content="View audit logs" position={Position.BOTTOM}>
                <Button icon="history" disabled={isSaving} onClick={this.handleShowAuditLogs} />
              </Tooltip>

              <Button
                icon="plus"
                intent="primary"
                disabled={isSaving}
                onClick={() => this.setState({ showNewTPAModal: true })}
              >
                New TPA
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  };

  renderTable = () => {
    const { tpas, quickFilterText, isLoading } = this.state;

    if (!tpas.length) {
      return (
        <Card elevation={Elevation.TWO} style={{ padding: "20px" }}>
          <div style={{ textAlign: "center", padding: "40px" }}>
            <H3 style={{ color: "var(--text-primary)", marginBottom: "20px" }}>
              {quickFilterText ? "No matching TPAs found" : "No TPAs configured"}
            </H3>
            <Text
              style={{
                color: "var(--text-secondary)",
                marginBottom: "var(--v3-spacing-xl)",
                fontSize: "var(--v3-font-size-md)",
                fontFamily: "var(--v3-font-family-sans)",
              }}
            >
              {quickFilterText
                ? "Try adjusting your search terms or clear the search to view all TPAs."
                : "Get started by creating your first TPA master record."}
            </Text>
            {quickFilterText ? (
              <Button icon="cross" intent="primary" onClick={() => this.setState({ quickFilterText: "" })}>
                Clear Search
              </Button>
            ) : (
              <Button
                icon="plus"
                intent="primary"
                large
                onClick={() => this.setState({ showNewTPAModal: true })}
                loading={this.state.isSaving}
              >
                Create First TPA
              </Button>
            )}
          </div>
        </Card>
      );
    }

    return (
      <Card elevation={Elevation.TWO} style={{ padding: "20px" }}>
        <div className="ag-theme-alpine-dark" style={{ height: "600px", width: "100%" }}>
          <AgGridReact
            columnDefs={this.columnDefs}
            rowData={tpas}
            onGridReady={this.onGridReady}
            quickFilterText={quickFilterText}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
            }}
            rowSelection="single"
            enableRangeSelection={true}
            enableCharts={true}
            sideBar={true}
            statusBar={{
              statusPanels: [
                {
                  statusPanel: "agTotalAndFilteredRowCountComponent",
                  align: "left",
                },
              ],
            }}
            loading={isLoading}
          />
        </div>
      </Card>
    );
  };

  render() {
    if (this.state.showAuditLogs) {
      return <AuditLogViewer onClose={this.handleCloseAuditLogs} />;
    }

    if (this.state.isLoading) {
      return (
        <div
          className="bp3-dark"
          style={{
            padding: "20px",
            backgroundColor: "var(--bg-primary)",
            color: "var(--text-primary)",
            minHeight: "100vh",
          }}
        >
          <NonIdealState
            icon={<Spinner size={50} />}
            title="Loading TPA Masters"
            description="Please wait while we load your TPA data and branch configurations..."
          />
        </div>
      );
    }

    return (
      <div
        className="bp3-dark"
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        {this.renderHeader()}
        {this.renderTable()}
        {this.renderNewTPAModal()}
        {this.renderEditModal()}
      </div>
    );
  }

  renderEditModal = () => {
    const { isEditModalOpen, editingTPA, branches, isSaving } = this.state;

    if (!isEditModalOpen || !editingTPA) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        onClick={this.closeEditModal}
      >
        <div
          style={{
            backgroundColor: "var(--bg-surface)",
            borderRadius: "8px",
            padding: "30px",
            maxWidth: "900px",
            width: "90%",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ marginBottom: "20px" }}>
            <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Edit TPA</H3>
            <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
              Update the TPA information and service configurations.
            </p>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
            {/* Row 1: Basic Info */}
            <ControlGroup>
              <Tag large minimal>
                TPA Name *
              </Tag>
              <InputGroup
                placeholder="Enter TPA name..."
                value={editingTPA.tpaName}
                onChange={(e) =>
                  this.setState({
                    editingTPA: { ...editingTPA, tpaName: e.target.value },
                  })
                }
                disabled={isSaving}
                intent={!editingTPA.tpaName.trim() ? Intent.DANGER : Intent.NONE}
                style={{ width: "250px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                TPA Code
              </Tag>
              <InputGroup
                placeholder="Enter TPA code..."
                value={editingTPA.tpaCode}
                onChange={(e) =>
                  this.setState({
                    editingTPA: { ...editingTPA, tpaCode: e.target.value },
                  })
                }
                disabled={isSaving}
                intent={Intent.NONE}
                style={{ width: "150px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Status
              </Tag>
              <Switch
                checked={editingTPA.isActive}
                onChange={(e) =>
                  this.setState({
                    editingTPA: { ...editingTPA, isActive: e.target.checked },
                  })
                }
                disabled={isSaving}
                label={editingTPA.isActive ? "Active" : "Inactive"}
                style={{ margin: "8px 0" }}
              />
            </ControlGroup>

            {/* Row 2: Contact Info */}
            <ControlGroup>
              <Tag large minimal>
                Contact Person
              </Tag>
              <InputGroup
                placeholder="Enter contact person name..."
                value={editingTPA.contactPerson}
                onChange={(e) =>
                  this.setState({
                    editingTPA: { ...editingTPA, contactPerson: e.target.value },
                  })
                }
                disabled={isSaving}
                style={{ width: "200px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Contact Phone
              </Tag>
              <InputGroup
                placeholder="Enter phone number..."
                value={editingTPA.contactPhone}
                onChange={(e) =>
                  this.setState({
                    editingTPA: { ...editingTPA, contactPhone: e.target.value },
                  })
                }
                disabled={isSaving}
                style={{ width: "180px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Contact Email
              </Tag>
              <InputGroup
                placeholder="Enter email address..."
                value={editingTPA.contactEmail}
                onChange={(e) =>
                  this.setState({
                    editingTPA: { ...editingTPA, contactEmail: e.target.value },
                  })
                }
                disabled={isSaving}
                style={{ width: "250px" }}
              />
            </ControlGroup>

            {/* Row 3: Address */}

            {/* Row 4: Address */}
            <div style={{ width: "100%" }}>
              <ControlGroup>
                <Tag large minimal>
                  Address
                </Tag>
                <TextArea
                  placeholder="Enter TPA address..."
                  value={editingTPA.address}
                  onChange={(e) =>
                    this.setState({
                      editingTPA: { ...editingTPA, address: e.target.value },
                    })
                  }
                  disabled={isSaving}
                  style={{ width: "100%", minHeight: "60px" }}
                />
              </ControlGroup>
            </div>

            {/* Row 5: Branches */}
            <div style={{ width: "100%", marginTop: "10px" }}>
              <ControlGroup>
                <Tag large minimal>
                  Branches *
                </Tag>
                <div style={{ flex: 1, minWidth: "400px" }}>
                  <NewMultiSelector
                    items={branches}
                    selectedItems={editingTPA.branches}
                    onChange={(selectedBranches) =>
                      this.setState({
                        editingTPA: { ...editingTPA, branches: selectedBranches },
                      })
                    }
                    placeholder="Select branches for this TPA..."
                    label="Branches"
                    showLabel={false}
                    maxDisplayItems={3}
                    sortItems={true}
                    emptyMessage="No branches available"
                    disabled={isSaving}
                  />
                </div>
              </ControlGroup>
            </div>

            {/* Row 6: Remarks */}
            <div style={{ width: "100%" }}>
              <ControlGroup>
                <Tag large minimal>
                  Remarks
                </Tag>
                <TextArea
                  placeholder="Enter any additional remarks..."
                  value={editingTPA.remarks}
                  onChange={(e) =>
                    this.setState({
                      editingTPA: { ...editingTPA, remarks: e.target.value },
                    })
                  }
                  disabled={isSaving}
                  style={{ width: "100%", minHeight: "60px" }}
                />
              </ControlGroup>
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Button large intent="none" icon="cross" onClick={this.closeEditModal}>
              Cancel
            </Button>
            <Button
              large
              intent="primary"
              icon="tick"
              onClick={this.updateTPAFromModal}
              loading={isSaving}
              disabled={isSaving || !editingTPA.tpaName.trim() || !editingTPA.branches.length}
            >
              Update TPA
            </Button>
          </div>
        </div>
      </div>
    );
  };

  renderNewTPAModal = () => {
    const { showNewTPAModal, newTPA, branches, isSaving } = this.state;

    if (!showNewTPAModal) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        onClick={() =>
          this.setState({
            showNewTPAModal: false,
            newTPA: {
              tpaName: "",
              tpaCode: "",
              contactPerson: "",
              contactPhone: "",
              contactEmail: "",
              address: "",
              isActive: true,
              branches: [],
              remarks: "",
            },
          })
        }
      >
        <div
          style={{
            backgroundColor: "var(--bg-surface)",
            borderRadius: "8px",
            padding: "30px",
            maxWidth: "900px",
            width: "90%",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ marginBottom: "20px" }}>
            <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Create New TPA</H3>
            <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
              Enter the TPA information and configure service parameters.
            </p>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
            {/* Row 1: Basic Info */}
            <ControlGroup>
              <Tag large minimal>
                TPA Name *
              </Tag>
              <InputGroup
                placeholder="Enter TPA name..."
                value={newTPA.tpaName}
                onChange={(e) =>
                  this.setState({
                    newTPA: { ...newTPA, tpaName: e.target.value },
                  })
                }
                disabled={isSaving}
                intent={!newTPA.tpaName.trim() ? Intent.DANGER : Intent.NONE}
                style={{ width: "250px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                TPA Code
              </Tag>
              <InputGroup
                placeholder="Enter TPA code..."
                value={newTPA.tpaCode}
                onChange={(e) =>
                  this.setState({
                    newTPA: { ...newTPA, tpaCode: e.target.value },
                  })
                }
                disabled={isSaving}
                intent={Intent.NONE}
                style={{ width: "150px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Status
              </Tag>
              <Switch
                checked={newTPA.isActive}
                onChange={(e) =>
                  this.setState({
                    newTPA: { ...newTPA, isActive: e.target.checked },
                  })
                }
                disabled={isSaving}
                label={newTPA.isActive ? "Active" : "Inactive"}
                style={{ margin: "8px 0" }}
              />
            </ControlGroup>

            {/* Row 2: Contact Info */}
            <ControlGroup>
              <Tag large minimal>
                Contact Person
              </Tag>
              <InputGroup
                placeholder="Enter contact person name..."
                value={newTPA.contactPerson}
                onChange={(e) =>
                  this.setState({
                    newTPA: { ...newTPA, contactPerson: e.target.value },
                  })
                }
                disabled={isSaving}
                style={{ width: "200px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Contact Phone
              </Tag>
              <InputGroup
                placeholder="Enter phone number..."
                value={newTPA.contactPhone}
                onChange={(e) =>
                  this.setState({
                    newTPA: { ...newTPA, contactPhone: e.target.value },
                  })
                }
                disabled={isSaving}
                style={{ width: "180px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Contact Email
              </Tag>
              <InputGroup
                placeholder="Enter email address..."
                value={newTPA.contactEmail}
                onChange={(e) =>
                  this.setState({
                    newTPA: { ...newTPA, contactEmail: e.target.value },
                  })
                }
                disabled={isSaving}
                style={{ width: "250px" }}
              />
            </ControlGroup>

            {/* Row 3: Address */}

            {/* Row 4: Address */}
            <div style={{ width: "100%" }}>
              <ControlGroup>
                <Tag large minimal>
                  Address
                </Tag>
                <TextArea
                  placeholder="Enter TPA address..."
                  value={newTPA.address}
                  onChange={(e) =>
                    this.setState({
                      newTPA: { ...newTPA, address: e.target.value },
                    })
                  }
                  disabled={isSaving}
                  style={{ width: "100%", minHeight: "60px" }}
                />
              </ControlGroup>
            </div>

            {/* Row 5: Branches */}
            <div style={{ width: "100%", marginTop: "10px" }}>
              <ControlGroup>
                <Tag large minimal>
                  Branches *
                </Tag>
                <div style={{ flex: 1, minWidth: "400px" }}>
                  <NewMultiSelector
                    items={branches}
                    selectedItems={newTPA.branches}
                    onChange={(selectedBranches) =>
                      this.setState({
                        newTPA: { ...newTPA, branches: selectedBranches },
                      })
                    }
                    placeholder="Select branches for this TPA..."
                    label="Branches"
                    showLabel={false}
                    maxDisplayItems={3}
                    sortItems={true}
                    emptyMessage="No branches available"
                    disabled={isSaving}
                  />
                </div>
              </ControlGroup>
            </div>

            {/* Row 6: Remarks */}
            <div style={{ width: "100%" }}>
              <ControlGroup>
                <Tag large minimal>
                  Remarks
                </Tag>
                <TextArea
                  placeholder="Enter any additional remarks..."
                  value={newTPA.remarks}
                  onChange={(e) =>
                    this.setState({
                      newTPA: { ...newTPA, remarks: e.target.value },
                    })
                  }
                  disabled={isSaving}
                  style={{ width: "100%", minHeight: "60px" }}
                />
              </ControlGroup>
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Button
              large
              intent="none"
              icon="cross"
              onClick={() =>
                this.setState({
                  showNewTPAModal: false,
                  newTPA: {
                    tpaName: "",
                    tpaCode: "",
                    contactPerson: "",
                    contactPhone: "",
                    contactEmail: "",
                    address: "",
                    creditLimit: 0,
                    creditDays: 30,
                    isActive: true,
                    branches: [],
                    serviceChargePercentage: 0,
                    remarks: "",
                  },
                })
              }
            >
              Cancel
            </Button>
            <Button
              large
              intent="primary"
              icon="tick"
              onClick={this.handleNewTPASubmit}
              loading={isSaving}
              disabled={isSaving || !newTPA.tpaName.trim() || !newTPA.branches.length}
            >
              Create TPA
            </Button>
          </div>
        </div>
      </div>
    );
  };
}

export default inject("authStore")(observer(TPAMaster));
